import { QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import type { Notification, Recommendation } from "@/model/notification";

import { NotificationStatus, QueryKeys } from "@/constants";

import { AxiosError } from "axios";
import useUserInfo from "./use-user-info";
import { store } from "@/store/store";
import { setNotificationUnreadCount } from "@/store/notification/notification-slice";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      retry(failureCount, error) {
        // Only retry up to 2 times for network errors
        if (error instanceof AxiosError) return failureCount < 2;
        return false;
      },
    },
  },
});
export const persister = createSyncStoragePersister({
  storage: window.sessionStorage,
  // serialize: (data) =>
  //   CryptoJS.AES.encrypt(JSON.stringify(data), secretKey).toString(),
  // deserialize: (encrypted) => {
  //   const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
  //   const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
  //   return JSON.parse(decryptedString);
  // },
});

export const updateNotificationStatusCache = (
  username: string,
  notification: Notification | Recommendation,
  status: NotificationStatus,
  queryKey:
    | QueryKeys.NOTIFICATIONS
    | QueryKeys.RECOMMENDATIONS = QueryKeys.NOTIFICATIONS,
) => {
  queryClient.setQueryData<(Notification | Recommendation)[]>(
    [queryKey, username],
    (old) => {
      if (!old) return old;

      const updated = old?.map((item) => {
        if (item.messageId !== notification?.messageId) return item;

        switch (status) {
          case NotificationStatus.LIKE:
            return { ...item, isLiked: true };
          case NotificationStatus.UNLIKE:
            return { ...item, isLiked: false };
          case NotificationStatus.SAVE:
            return { ...item, saveFlag: true };
          case NotificationStatus.UNSAVE:
            return { ...item, saveFlag: false };
          case NotificationStatus.OPEN:
            return { ...item, isRead: true };
          default:
            return item;
        }
      });

      if (
        queryKey === QueryKeys.NOTIFICATIONS &&
        status === NotificationStatus.OPEN &&
        !notification.isRead
      ) {
        const unread = updated.filter((item) => !item.isRead).length;
        store.dispatch(setNotificationUnreadCount(unread));
      }

      return updated;
    },
  );
};

/**
 * Custom hook that returns a function to update the name of a specific notification action button in the cache.
 *
 * Unlike typical hooks, this does not perform any update immediately on render.
 * Instead, it returns a function that can be called when needed (e.g., on form submit).
 *
 * This avoids breaking the Rules of Hooks by deferring the side effect (queryClient update)
 * to a safe context like an event handler.
 */
export const useUpdateNotificationActionButtonName = () => {
  const { username } = useUserInfo();

  return (trackId: number, templateId: number, newButtonName: string) => {
    queryClient.setQueryData<Notification[]>(
      [QueryKeys.NOTIFICATIONS, username],
      (oldData) => {
        if (!oldData) return oldData;

        return oldData.map((notification) => {
          if (
            notification.outgoingTrackId !== trackId ||
            !notification.actionButton
          ) {
            return notification;
          }

          const updatedButtons = notification.actionButton.map((button) => {
            if (button?.templateId === templateId) {
              return { ...button, name: newButtonName };
            }
            return button;
          });

          return { ...notification, actionButton: updatedButtons };
        });
      },
    );
  };
};

export const invalidateCachedQuery = (
  queryKey: string,
  queryVariable?: string | number,
) => {
  if (!queryClient || !queryKey.trim()) return;

  const fullKey =
    queryVariable !== undefined ? [queryKey, queryVariable] : [queryKey];

  try {
    queryClient.invalidateQueries({ queryKey: fullKey });
    // console.log("invalidateCachedQuery: Query invalidated:", fullKey);
  } catch (error) {
    console.error("invalidateCachedQuery: Failed to invalidate query.", error);
  }
};

{
  /* 
| Property        | `staleTime`                               | `cacheTime`                                                             |
| --------------- | ----------------------------------------- | ----------------------------------------------------------------------- |
| Purpose         | How long data is considered **fresh**     | How long unused data stays in **memory**                                |
| Default         | `0` (stale immediately)                   | `5 minutes`                                                             |
| Refetch?        | ✅ **No** refetch during fresh period      | ❌ **No** effect on refetching                                           |
| Memory?         | ❌ No effect on memory retention           | ✅ Keeps data in memory for reuse                                        |
| When it matters | When **avoiding refetching** is important | When **preserving cache** across pages or back/forward nav is important |

*/
}
